// components/ActivityLogModal.jsx
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Modal, Box, Button } from "@mui/material";

export default function ActivityLogModal({
  open,
  onClose,
  activityLogs = [],
  prNo,
  handleViewAttachment, // ✅ added
}) {
  const sortedLogs = useMemo(() => {
    return (activityLogs || [])
      .slice()
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [activityLogs]);

  const formatTimestamp = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const getBgColorAndLabel = (action) => {
    if (!action) return { bg: "bg-gray-100 dark:bg-gray-700", label: "Update" };
    const a = action.toLowerCase();
    if (a.includes("po")) return { bg: "bg-blue-50 dark:bg-blue-800", label: "PO Update" };
    if (a.includes("gr") || a.includes("invoice"))
      return { bg: "bg-green-50 dark:bg-green-800", label: "GR / Invoice Update" };
    if (a.includes("receipt") || a.includes("payment"))
      return { bg: "bg-orange-50 dark:bg-yellow-800", label: "Payment Update" };
    return { bg: "bg-gray-100 dark:bg-gray-700", label: action };
  };

  const buildCompletionMessages = (changes = {}) => {
    const messages = [];
    if (!changes || typeof changes !== "object") return messages;

    const safeGet = (k) =>
      changes[k] ??
      changes[k.toLowerCase()] ??
      changes[k.charAt(0).toLowerCase() + k.slice(1)];

    if (safeGet("grFully")?.new === true) messages.push("GR completed fully");
    if (safeGet("grPartially")?.new === true) messages.push("GR completed partially");
    if (safeGet("invoiceFully")?.new === true) messages.push("Invoice completed fully");
    if (safeGet("invoicePartially")?.new === true)
      messages.push("Invoice completed partially");

    return messages;
  };

  const shouldRenderChip = (key, displayValue) => {
    if (displayValue == null) return false;
    if (typeof displayValue === "string" && displayValue.trim() === "") return false;

    const kl = key.toLowerCase();
    if (kl.includes("fully") || kl.includes("partially")) return false;

    return true;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            Activity Logs{prNo ? ` - PR No: ${prNo}` : ""}
          </h3>
          <Button size="small" onClick={onClose}>Close</Button>
        </div>

        <div className="p-4 space-y-4">
          {sortedLogs.length === 0 ? (
            <div className="text-center text-sm text-gray-500">No activity found</div>
          ) : (
            sortedLogs.map((act) => {
              const { bg, label } = getBgColorAndLabel(act.action);
              const completionMessages = buildCompletionMessages(act.changes || {});

              return (
                <div key={act._id} className={`border rounded-lg p-4 ${bg}`}>
                  <div className="flex justify-between mb-2">
                    <span className="font-semibold">{act.name || "Unknown"}</span>
                    <span className="text-xs text-gray-500">
                      {formatTimestamp(act.timestamp)}
                    </span>
                  </div>

                  <div className="font-semibold mb-2">{label}</div>

                  {completionMessages.length > 0 && (
                    <div className="flex gap-2 mb-2">
                      {completionMessages.map((m, i) => (
                        <span key={i} className="px-2 py-1 bg-white rounded text-sm">
                          {m}
                        </span>
                      ))}
                    </div>
                  )}

                  {act.changes && (
                    <div className="flex flex-wrap gap-2 text-xs">
                      {Object.entries(act.changes).map(([key, val]) => {
                        let displayValue = val?.new ?? val;
                        if (!shouldRenderChip(key, displayValue)) return null;

                        return (
                          <span key={key} className="px-2 py-1 bg-white rounded">
                            <b>{key}:</b> {displayValue}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* ✅ FIXED ATTACHMENTS */}
                  {act.uploadedFiles?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs mb-1">Uploaded files:</div>
                      <div className="flex gap-2 flex-wrap">
                        {act.uploadedFiles.map((f) => (
                          <span
                            key={f._id || f.filename}
                            onClick={() => handleViewAttachment(f)} // ✅ SAME LOGIC
                            className="text-xs px-3 py-1 border rounded cursor-pointer hover:bg-gray-100 text-blue-600"
                          >
                            {f.originalname || f.filename}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="p-3 border-t flex justify-end">
          <Button size="small" onClick={onClose}>Close</Button>
        </div>
      </Box>
    </Modal>
  );
}

ActivityLogModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activityLogs: PropTypes.array,
  prNo: PropTypes.string,
  handleViewAttachment: PropTypes.func, // ✅ added
};